import { Router, Log } from '@lightningjs/sdk'
import { appSettings } from '../AppSettings'

import BrandsAsset from '../assets/brand/BrandsAsset'
import CatchUpAsset from '../assets/CatchUpAsset'
import PlaylistsAsset from '../assets/playlist/PlaylistsAsset'
import PodcastListAsset from '../assets/podcast/PodcastListAsset'

export const api = {
  domains: {
    Base: 'https://bff-tv-guacamole.musicradio.com/v1',
    webSocket: 'wss://metadata.musicradio.com/v2/now-playing',
  },
  fetchTimeout: 5000,
  socketInstance: null,
}

// https://bff-web-guacamole.musicradio.com/features/podcasts

export const exponentialBackoff = (toTry, max, delay, onSuccess, onError, indefinite = false) => {
  const result = toTry()
  if (result) {
    onSuccess()
  } else {
    if (max > 0) {
      setTimeout(() => {
        const randomExtraTime = Math.floor(Math.random() * 3) * 1000
        exponentialBackoff(
          toTry,
          --max,
          delay * 2 + randomExtraTime,
          onSuccess,
          onError,
          indefinite
        )
      }, delay)
    } else {
      if (indefinite) {
        if (max === 0) {
          onError()
        }
        setTimeout(() => {
          exponentialBackoff(toTry, --max, delay, onSuccess, onError, indefinite)
        }, delay)
      } else {
        onError()
      }
    }
  }
}

export const loadBrands = brand => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  return fetch(`${api.domains.Base}/brands/${brand ? '/' + brand : ''}`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      return new BrandsAsset(result.data)
    })
    .catch(error => {
      Log.error('Error fetching Brands:', error)
    })
}

export const loadBrandStations = () => {
  const brand = appSettings.currentBrand.brandName
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  return fetch(`${api.domains.Base}/brands${brand ? '/' + brand : ''}`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      appSettings.currentBrand.addStations(result.data.brand)
      appSettings.currentStation = appSettings.currentBrand.stations[0]

      setTimeout(() => {
        Router.resume()
      }, 1000)
    })
    .catch(error => {
      Log.error('Error fetching Brands', error)
    })
}

export const getCatchUp = (brandData, tStamp) => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  const currentBrand = appSettings.currentBrand
  return fetch(
    `${api.domains.Base}/catchups/${currentBrand.brandName}/${currentBrand.nationalStation.safeName}/`,
    requestOptions
  )
    .then(response => {
      return response.json()
    })
    .then(result => {
      return {
        data: new CatchUpAsset(result),
        timestamp: tStamp,
      }
    })
    .catch(error => {
      Log.error('Error fetching catchUp', error)
    })
}

export const getPlaylists = () => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  return fetch(`${api.domains.Base}/features/playlists/`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      return new PlaylistsAsset(result)
    })
    .catch(error => {
      Log.error('Error fetching playlist', error)
    })
}

export const getPodcastList = () => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
    headers: { 'content-type': 'application/json' },
  }
  return fetch(`${api.domains.Base}/features/podcasts/`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      return new PodcastListAsset(result)
    })
    .catch(error => {
      Log.error('Error fetching Podcasts', error)
    })
}

export const getPodcastSearch = (searchTerm, timestamp) => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  return fetch(`${api.domains.Base}/podcasts/search/?query=${searchTerm}/`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      result.type = 'podcast'

      return {
        searchTimeStamp: timestamp,
        searchResult: result,
      }
    })
    .catch(() => {
      return {
        searchTimeStamp: timestamp,
        searchResult: [],
      }
    })
}

export const getPlaylistSearch = (searchTerm, timestamp) => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  return fetch(`${api.domains.Base}/playlists/search/?query=${searchTerm}/`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      result.type = 'playlist'

      return {
        searchTimeStamp: timestamp,
        searchResult: result,
      }
    })
    .catch(() => {
      return {
        searchTimeStamp: timestamp,
        searchResult: [],
      }
    })
}

export const getEpisodeData = (dataType, dataId) => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  let url = ''

  if (dataType === 'catchUp') {
    url = `${api.domains.Base}/catchups/${dataId}/`
  } else if (dataType === 'podcast') {
    url = `${api.domains.Base}/podcasts/${dataId}/`
  } else {
    return {
      dataType,
      dataId,
      data: [],
    }
  }

  return fetch(url, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      return {
        dataType,
        dataId,
        data: result,
      }
    })
    .catch(() => {
      return {
        dataType,
        dataId,
        data: [],
      }
    })
}

export const getPlaylistData = playlistId => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  return fetch(`${api.domains.Base}/playlists/${playlistId}/`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      return result
    })
    .catch(() => {
      return {}
    })
}

export const getPodcastData = podcastId => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  return fetch(`${api.domains.Base}/podcasts/${podcastId}/`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      return result
    })
    .catch(() => {
      return {}
    })
}

export const getEpisode = episodeId => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  return fetch(`${api.domains.Base}/podcasts/episodes/${episodeId}/`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      return result
    })
    .catch(() => {
      return {}
    })
}

export const getPlaylist = plId => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  return fetch(`${api.domains.Base}/playlists/${plId}/`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      return result
    })
    .catch(() => {
      return {}
    })
}

export const getCatchupEpisode = (chatchId, episodeId) => {
  const requestOptions = {
    method: 'GET',
    redirect: 'follow',
  }

  return fetch(`${api.domains.Base}/catchups/${chatchId}/`, requestOptions)
    .then(response => {
      return response.json()
    })
    .then(result => {
      if (result && result.episodes && result.episodes.length > 0) {
        const episode = result.episodes.filter(ep => {
          return ep.id === episodeId
        })

        if (episode && episode.length > 0) {
          return episode[0]
        } else {
          return {}
        }
      } else {
        return {}
      }
    })
    .catch(() => {
      return {}
    })
}

export const getSearch = (searchTerm, timestamp) => {
  const promSearchtems = [
    getPodcastSearch(searchTerm, timestamp),
    getPlaylistSearch(searchTerm, timestamp),
  ]

  return Promise.all(promSearchtems).then(response => {
    return response
  })
}
